import { Component, EventEmitter, Input, OnInit, Output, forwardRef } from '@angular/core';
import { ControlValueAccessor, FormBuilder, FormGroup, NG_VALUE_ACCESSOR, Validators } from '@angular/forms';
import { RDMClientService } from '@shared-services/rdm-client.service';
import { HttpErrorHandlerService, MessageType } from '@shared-services/http-error-handling.service';

@Component({
  selector: 'app-rdm-select',
  templateUrl: './rdm-select.component.html',
  styleUrls: ['./rdm-select.component.less'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => RdmSelectComponent),
      multi: true
    }
  ]
})
export class RdmSelectComponent implements OnInit, ControlValueAccessor {
  @Input() options: { code: string; label: string }[] = [];
  @Input() placeholder = 'Select';
  @Input() rdmTypeLabel!: string;
  @Input() nzMode: 'default' | 'multiple' | 'tags' = 'default';
  @Input() allowClear = true;
  @Input() allowAdd = false;
  @Input() allowAddPermission: string = '';
  @Input() allowAddModule: string = '';

  value: any = null;
  isDisabled = false;
  searchText = '';
  modalVisible = false;
  modalLoading = false;
  addForm!: FormGroup;
  autoGenerateCode = true;
  refTypeMap: Record<string, number> = {};
  private onChange = (_: any) => { };
  private onTouched = () => { };

  constructor(
    private fb: FormBuilder,
    private rdmService: RDMClientService,
    private httpMessages: HttpErrorHandlerService
  ) { }

  ngOnInit(): void {
    this.addForm = this.fb.group({
      code: ['', [Validators.required, Validators.minLength(3), Validators.maxLength(100)]],
      description: ['', [Validators.required, Validators.minLength(3), Validators.maxLength(200)]]
    });
    console.log("this.refTypeMap",this.rdmTypeLabel);
    this.rdmService.getRefTypeMaster().subscribe({
      next: (response: any[]) => {
        this.modalLoading = false;

        this.refTypeMap = response.reduce((acc: Record<string, number>, item) => {
          acc[item.label] = item.id;
          return acc;
        }, {});

        console.log(this.refTypeMap);
        // Output:
        // {
        //   "kc-profile-category-code": -40,
        //   "kc-profile-risk-severity": -39
        // }
      },
      error: (err: any) => {
        this.modalLoading = false;
        this.httpMessages.showTopFloatMessage(
          err?.error,
          MessageType.ERROR,
          'Failed to add value'
        );
      }
    });

  }

  writeValue(val: any): void { this.value = val; }
  registerOnChange(fn: any): void { this.onChange = fn; }
  registerOnTouched(fn: any): void { this.onTouched = fn; }
  setDisabledState(isDisabled: boolean): void { this.isDisabled = isDisabled; }

  @Output() blur = new EventEmitter<void>();

  onModelChange(val: any): void {
    this.value = val;
    this.onChange(val);
    this.onTouched();
  }

  onBlur(): void {
    this.onTouched();
    this.blur.emit();
  }

  onSearch(text: string): void {
    this.searchText = text?.trim() || '';
  }

  get canAdd(): boolean {
    return false; // Temporarily disable the add functionality until Confirm by siva.
    // if (this.allowAdd) return true;
    // if (!this.allowAddPermission || !this.allowAddModule) return false;
    // return this.hasPermission(this.allowAddPermission, this.allowAddModule);
  }

  private hasPermission(action: string, module: string): boolean {
    try {
      const moduleSet = JSON.parse(sessionStorage.getItem('modules') || '[]');
      const moduleObj = this.findModule(moduleSet, module);
      return moduleObj?.actions?.some((a: string) => a.toLowerCase() === action.toLowerCase()) || false;
    } catch {
      return false;
    }
  }

  private findModule(module: any, label: string): any {
    if (module?.label === label) return module;
    const items = module?.length ? module : module?.children;
    if (!items) return null;
    for (let i = 0; i < items.length; i++) {
      const found = this.findModule(items[i], label);
      if (found) return found;
    }
    return null;
  }

  get generatedCode(): string {
    const prefix = (this.rdmTypeLabel || '').replace(/-/g, '');
    const suffix = Date.now().toString();
    return (prefix + suffix).slice(0, 100);
  }

  toggleAutoGenerate(auto: boolean): void {
    this.autoGenerateCode = auto;
    const codeCtrl = this.addForm.get('code')!;
    if (auto) {
      codeCtrl.setValue(this.generatedCode);
      codeCtrl.disable();
    } else {
      codeCtrl.setValue('');
      codeCtrl.enable();
    }
  }

  regenerateCode(): void {
    this.addForm.get('code')!.setValue(this.generatedCode);
  }

  openModal(): void {
    this.autoGenerateCode = true;
    this.addForm.reset({ code: this.generatedCode, description: this.searchText });
    this.addForm.get('code')!.disable();
    this.modalVisible = true;
  }

  closeModal(): void {
    this.modalVisible = false;
    this.addForm.get('code')!.enable();
    this.addForm.reset();
  }

  submit(): void {
    const validFrom = new Date().toISOString().split('T')[0];
    const validToDate = new Date(validFrom);
    validToDate.setFullYear(validToDate.getFullYear() + 100);

    const validTo = validToDate.toISOString().split('T')[0];
    if (this.addForm.get('description')!.invalid) return;
    if (!this.autoGenerateCode && this.addForm.get('code')!.invalid) return;
    const code = this.addForm.getRawValue().code; // getRawValue includes disabled controls
    const description = this.addForm.value.description;
    this.modalLoading = true;
    const payload = {
      refTypeId: this.refTypeMap[this.rdmTypeLabel],
      code: code,
      label: description,
      description: description,
      otherDescription: null,
      parentRefValue: null,
      sequence: null,
      validFrom: validFrom,
      validTo:validTo,
      effectiveDateFrom: null
    }
    this.rdmService.setRefTypeMaster(payload).subscribe({
      next: () => {
        this.modalLoading = false;
        this.httpMessages.showTopFloatMessage(`"${description}" added successfully`, MessageType.SUCCESS);
        this.options = [...this.options, { code, label: description }];
        this.onModelChange(code);
        this.searchText = '';
        this.closeModal();
      },
      error: (err: any) => {
        this.modalLoading = false;
        this.httpMessages.showTopFloatMessage(err?.error, MessageType.ERROR, 'Failed to add option');
      }
    });
  }
}
